import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import countryPriceApi from "../../../api/countryPriceApi";
import resellingPriceApi from "../../../api/resellingPriceApi";

const SellFrymes = () => {
  const [priceRange, setPriceRange] = useState(null);
  const [l1PriceData, setL1PriceData] = useState(null);
  const [existingPricing, setExistingPricing] = useState(null);
  const [franchiseDetails, setFranchiseDetails] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState("");
  const [visibility, setVisibility] = useState([]);

  const countryCode = Cookies.get("country");
  const email = Cookies.get("userEmail");
  // ROLE LOGIC
  const isParentCookie = Cookies.get("isParent") === "true";

  const role = isParentCookie ? "L1_ADMIN" : "L2_ADMIN";

  // FETCH L1 PRICE
  const fetchL1Price = async () => {
    if (!isParentCookie && email) {
      try {
        const res = await resellingPriceApi.getParentPrices(email);
        if (res.data && res.data.pricing && res.data.pricing.sellingPrice) {
          setL1PriceData({
            ...res.data.pricing,
            parentId: res.data.parentId,
          });
        }
      } catch (err) {
        console.error("Error fetching L1 price:", err);
      }
    }
  };

  // FETCH PRICE
  const fetchPrices = async () => {
    if (!countryCode || !email) {
      setError("Country code or email not found in cookies");
      return;
    }

    try {
      const countryRes = await countryPriceApi.getCountryPriceByCodeAndRole(
        countryCode,
        role,
      );
      const data = Array.isArray(countryRes.data)
        ? countryRes.data[0]
        : countryRes.data;
      setPriceRange(data || null);

      try {
        const sellerRes = await resellingPriceApi.getSellerDetails(email);
        if (sellerRes.data && sellerRes.data.franchise) {
          setFranchiseDetails(sellerRes.data.franchise);
        }
        if (
          sellerRes.data &&
          sellerRes.data.resellerPricing &&
          sellerRes.data.resellerPricing.length > 0
        ) {
          setExistingPricing(sellerRes.data.resellerPricing[0]);
        } else {
          setExistingPricing(null);
        }
      } catch (sellerErr) {
        console.log("No reseller pricing found or error:", sellerErr);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch price range");
    }
  };

  useEffect(() => {
    fetchPrices();
    fetchL1Price();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode, role, email]);

  // SET DEFAULT VISIBILITY FOR L2
  useEffect(() => {
    if (!isParentCookie) {
      setVisibility(["CUSTOMER"]);
    }
  }, [isParentCookie]);

  const activeBasePrice = l1PriceData
    ? l1PriceData.sellingPrice
    : priceRange?.minPrice;

  const activeCurrencySymbol =
    priceRange?.currencySymbol || l1PriceData?.currencySymbol;

  const handleVisibilityChange = (e) => {
    const value = e.target.value;

    setVisibility((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!priceRange && !l1PriceData) {
      return;
    }

    if (!selectedPrice) {
      toast.warning("Please enter selling price");
      return;
    }

    if (Number(selectedPrice) < Number(activeBasePrice)) {
      toast.error(`Price must be greater than or equal to ${activeBasePrice}`);
      return;
    }

    if (visibility.length === 0) {
      toast.warning("Please select at least one visibility role");
      return;
    }

    try {
      const payload = {
        id: existingPricing?.id,
        countryCode: countryCode,
        sellingPrice: Number(selectedPrice),
        visibleToRoles: visibility,
      };

      console.log("Submitting pricing:", payload);
      console.log("isEditing:", isEditing);
      console.log("existingPricing:", existingPricing);
      console.log("existingPricing.id:", existingPricing?.id);
      

      let response;

      if (isEditing && existingPricing?.id) {
        // =========================
        // UPDATE EXISTING PRICE
        // =========================
        response = await resellingPriceApi.updatePrice(
          existingPricing.id,
          email,
          payload,
        );

        toast.success("Price updated successfully!");
      } else {
        // =========================
        // CREATE NEW PRICE
        // =========================
        response = await resellingPriceApi.setPrice(email, payload);

        toast.success("Price submitted successfully!");
      }

      console.log("API response:", response.data);

      // Refresh from backend
      await fetchPrices();

      // Reset
      setSelectedPrice("");
      setVisibility([]);
      setIsEditing(false);
    } catch (err) {
      console.error("Pricing error:", err);

      toast.error(
        err.response?.data?.message ||
          err.response?.data ||
          "Failed to save pricing",
      );
    }
  };
  // VISIBILITY OPTIONS BASED ON ROLE
  const visibilityOptions = isParentCookie
    ? ["L2_ADMIN", "CUSTOMER"] // L1 admin sees these
    : ["CUSTOMER"]; // L2 admin only customer

  return (
    <div className="container mt-4">
      <div className="card shadow p-4">
        <h2 className="mb-3">
          Frymes pricing for{" "}
          <span className="text-primary">
            {franchiseDetails?.organization || "Organization"}
          </span>{" "}
          at{" "}
          <span className="text-primary">
            {franchiseDetails?.location || "Location"}
          </span>{" "}
          in the country{" "}
          <span className="text-primary">
            {franchiseDetails?.country || countryCode}
          </span>
        </h2>

        {error && <div className="alert alert-danger">{error}</div>}

        {priceRange || l1PriceData ? (
          <>
            {/* BASE PRICE INFO */}
            <div className="mb-3">
              {l1PriceData && (
                <div className="alert alert-info mb-1">
                  Your Franchise L1 Admin Base Price:{" "}
                  <strong>
                    {l1PriceData.sellingPrice} {activeCurrencySymbol} (
                    {l1PriceData.countryCode})
                  </strong>
                </div>
              )}
              {priceRange && (
                <div className="alert alert-info mb-1">
                  Master Admin Base Price:{" "}
                  <strong>
                    {priceRange.minPrice} {activeCurrencySymbol} (
                    {priceRange.currencyCode})
                  </strong>
                </div>
              )}
            </div>

            {existingPricing && !isEditing ? (
              <div>
                <Table striped bordered hover className="mb-3">
                  <thead>
                    <tr>
                      <th>Base Price</th>
                      <th>Selling Price</th>
                      <th>Profit per Fryme</th>
                      <th>Visible To</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        {activeBasePrice} {activeCurrencySymbol}
                      </td>
                      <td>
                        {existingPricing.sellingPrice} {activeCurrencySymbol}
                      </td>
                      <td className="text-success fw-bold">
                        {(
                          existingPricing.sellingPrice - activeBasePrice
                        ).toFixed(2)}{" "}
                        {activeCurrencySymbol}{" "}
                        {activeBasePrice > 0
                          ? `(${(((existingPricing.sellingPrice - activeBasePrice) / activeBasePrice) * 100).toFixed(2)}%)`
                          : ""}
                      </td>
                      <td>
                        {existingPricing.visibleToRoles
                          ?.filter(Boolean)
                          .map((r) =>
                            r === "CUSTOMER" ? "Customer" : "L2 Admin",
                          )
                          .join(", ") || "N/A"}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => {
                            console.log("Editing pricing:", existingPricing);

                            setSelectedPrice(existingPricing.sellingPrice);

                            setVisibility(existingPricing.visibleToRoles || []);

                            setIsEditing(true);
                          }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label">Enter Price</label>

                  <input
                    type="number"
                    className="form-control"
                    value={selectedPrice}
                    onChange={(e) => setSelectedPrice(e.target.value)}
                  />

                  {/* MUTED TEXT */}
                  <small className="text-muted">
                    You can set your price above the base price or equal to base
                    price
                  </small>
                </div>

                {/* VISIBILITY */}
                <div className="mb-3">
                  <label className="form-label">Visibility</label>

                  {visibilityOptions.map((role) => (
                    <div className="form-check" key={role}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        value={role}
                        checked={visibility.includes(role)}
                        onChange={handleVisibilityChange}
                      />
                      <label className="form-check-label">
                        {role === "CUSTOMER" ? "Customer" : "L2 Admin"}
                      </label>
                    </div>
                  ))}
                </div>

                <button type="submit" className="btn btn-primary">
                  {isEditing ? "Update Price" : "Submit Price"}
                </button>

                {existingPricing && isEditing && (
                  <button
                    type="button"
                    className="btn btn-secondary ms-2"
                    onClick={() => {
                      setIsEditing(false);
                      setSelectedPrice("");
                      setVisibility([]);
                    }}
                  >
                    Cancel
                  </button>
                )}
              </form>
            )}
          </>
        ) : !error ? (
          <div className="alert alert-warning">
            Please contact your respective franchise for base price.
          </div>
        ) : (
          <p className="text-muted">Loading...</p>
        )}
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
      />
    </div>
  );
};

export default SellFrymes;
